import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ForecastProvider_V2 } from './context/ForecastContext_V2';
import { AuthProvider, useAuth } from './context/AuthContext';
import type { UserPermissions } from './context/AuthContext';
import { activateWebSecurityShield } from './utils/screenSecurityShield';

import { ToolsLayout_V2 } from './layouts/ToolsLayout_V2';

import { FinancialMatrix_V2 } from './pages/Tools/FinancialMatrix_V2';
import { GraphicAnalysis_V2 } from './pages/Tools/GraphicAnalysis_V2';
import { LiquidationsGraphicAnalysis_V2 } from './pages/Tools/LiquidationsGraphicAnalysis_V2';
import { LiquidationsAuditPdf_V2 } from './pages/Tools/LiquidationsAuditPdf_V2';
import { SpaghettiMap_V2 } from './pages/Tools/SpaghettiMap_V2';
import { AuditLedger_V2 } from './pages/Tools/AuditLedger_V2';
import { AuditEngine_V2 } from './pages/Tools/AuditEngine_V2';
import { AuditFinal_V2 } from './pages/Tools/AuditFinal_V2';
import { MultiCotizador_V2 } from './pages/Tools/MultiCotizador_V2';
import { SystemFlowchartViewer_V2 } from './pages/Tools/SystemFlowchartViewer_V2';
// [HIDDEN FROM UI] import { StaticVsDynamicPortCost } from './pages/Tools/StaticVsDynamicPortCost_V2';
import { SystemDocumentation_V2 } from './pages/Tools/SystemDocumentation_V2';
import { CompanyOrganigram_V2 } from './pages/Tools/CompanyOrganigram_V2';
import { MofManual_V2 } from './pages/Tools/MofManual_V2';
import { SotaPetralOne_V2 } from './pages/Tools/SotaPetralOne_V2';
import { VoyageLiquidator_V2 } from './pages/Tools/VoyageLiquidator_V2';


import { VesselsMaster } from './pages/Masters/VesselsMaster_V2';
import { RoutesMaster } from './pages/Masters/RoutesMaster_V2';
import { RouteMaster_V2 } from './components/Masters/RouteMaster_V2';
import { ClientsMaster } from './pages/Masters/ClientsMaster_V2';
import { ContractsMaster } from './pages/Masters/ContractsMaster_V2';
import { BudgetsMaster } from './pages/Masters/BudgetsMaster_V2';
import { FinancialProjectionsMaster } from './pages/Masters/FinancialProjectionsMaster_V2';
import { PortsMaster_V2 } from './pages/Masters/PortsMaster_V2';
import { PortCostsMaster_V2 } from './pages/Masters/PortCostsMaster_V2';
import { DemurrageMaster_V2 } from './pages/Masters/DemurrageMaster_V2';
import { PortTariffsMaster } from './pages/Masters/PortTariffsMaster';
import { SourcesSinksMaster_V2 } from './pages/Masters/SourcesSinksMaster_V2';
import { BunkerMaster } from './pages/Masters/BunkerMaster';
import { BunkerMasterBootstrapping } from './pages/Masters/BunkerMasterBootstrapping';
import { Login } from './pages/Auth/Login';
import { UsersPermissions } from './pages/Auth/UsersPermissions';
import { DeviceVaultMaster } from './pages/Masters/DeviceVaultMaster';
import { AuditMaster_V2 } from './pages/Masters/AuditMaster_V2';
import { CommercialTrashMaster_V2 } from './pages/Masters/CommercialTrashMaster_V2';

// Componente para proteger rutas privadas y validar permisos específicos
const ProtectedRoute = ({ 
    children, 
    module,
    requiredAccess = 'Visor',
    requireAdmin = false
}: { 
    children: React.ReactNode;
    module?: keyof UserPermissions;
    requiredAccess?: 'Editor' | 'Visor';
    requireAdmin?: boolean;
}) => {
    const { user, isAuthenticated, hasPermission, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen w-screen flex items-center justify-center bg-slate-900">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }
    
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace={false} />;
    }
    
    if (requireAdmin && user.role !== 'ADMIN') {
        return <Navigate to="/dashboard" replace={false} />;
    }
    
    if (module && !hasPermission(module, requiredAccess)) {
        return <Navigate to="/dashboard" replace={false} />;
    }
    
    return <>{children}</>;
};

// Componente público de redirección inmediata a la presentación Handshake (Sin 2FA)
const HandshakeRedirect: React.FC = () => {
    useEffect(() => {
        window.location.replace('/handshake/index.html');
    }, []);
    return (
        <div className="min-h-screen w-screen flex items-center justify-center bg-slate-950 text-white font-mono text-sm">
            <div className="animate-spin h-6 w-6 border-2 border-sky-400 border-t-transparent rounded-full mr-3"></div>
            <span>Cargando Protocolo Handshake...</span>
        </div>
    );
};

function App_V2() {
  useEffect(() => {
    const cleanup = activateWebSecurityShield({
      enableClipboardShield: true,
      enableFocusBlur: false,
      enableInspectorBlock: false
    });
    return () => cleanup();
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">

        <AuthProvider>
          <ForecastProvider_V2>
            <Routes>
              {/* Rutas Públicas Sin 2FA */}
              <Route path="/login" element={<Login />} />
              <Route path="/handshake" element={<HandshakeRedirect />} />
              
              {/* Maestros Protegidos */}
              <Route path="/vessels" element={<ProtectedRoute module="maestro_buques"><VesselsMaster /></ProtectedRoute>} />
              <Route path="/routes" element={<ProtectedRoute module="maestro_rutas"><RoutesMaster /></ProtectedRoute>} />
              <Route path="/spot-routes" element={<ProtectedRoute module="maestro_rutas"><RouteMaster_V2 mode="routes" /></ProtectedRoute>} />
              <Route path="/quotes" element={<ProtectedRoute module="maestro_rutas"><RouteMaster_V2 mode="quotes" /></ProtectedRoute>} />
              <Route path="/budgets" element={<ProtectedRoute module="maestro_contratos"><BudgetsMaster /></ProtectedRoute>} />
              <Route path="/financial-projections" element={<ProtectedRoute><FinancialProjectionsMaster /></ProtectedRoute>} />

              <Route path="/clients" element={<ProtectedRoute module="maestro_tarifas"><ClientsMaster /></ProtectedRoute>} />
              <Route path="/contracts" element={<ProtectedRoute module="maestro_contratos"><ContractsMaster /></ProtectedRoute>} />
              <Route path="/ports" element={<ProtectedRoute module="maestro_puertos"><PortsMaster_V2 /></ProtectedRoute>} />
              <Route path="/port-costs" element={<ProtectedRoute module="maestro_costos_agencia"><PortCostsMaster_V2 /></ProtectedRoute>} />
              <Route path="/demurrage" element={<ProtectedRoute module="maestro_costos_agencia"><DemurrageMaster_V2 /></ProtectedRoute>} />
              <Route path="/port-tariffs" element={<ProtectedRoute module="maestro_costos_agencia"><PortTariffsMaster /></ProtectedRoute>} />
              <Route path="/sources-sinks" element={<ProtectedRoute module="maestro_rutas"><SourcesSinksMaster_V2 /></ProtectedRoute>} />
              <Route path="/bunker-prices" element={<ProtectedRoute module="maestro_bunker"><BunkerMaster /></ProtectedRoute>} />
              <Route path="/bunker-bootstrapping" element={<ProtectedRoute module="maestro_bunker"><BunkerMasterBootstrapping /></ProtectedRoute>} />
              <Route path="/commercial-trash" element={<ProtectedRoute><CommercialTrashMaster_V2 /></ProtectedRoute>} />
              
              {/* Herramientas Protegidas (Ribbon via ToolsLayout) */}
              <Route element={<ProtectedRoute><ToolsLayout_V2 /></ProtectedRoute>}>
                  <Route path="/multicotizador" element={<MultiCotizador_V2 />} />
                  <Route path="/dashboard" element={<FinancialMatrix_V2 />} />
                  <Route path="/graphic-analysis" element={<GraphicAnalysis_V2 />} />
                  <Route path="/liquidations-graphic-analysis" element={<LiquidationsGraphicAnalysis_V2 />} />
                  <Route path="/liquidations-pdf-audit" element={<LiquidationsAuditPdf_V2 />} />
                  <Route path="/spaghetti-map" element={<SpaghettiMap_V2 />} />
                  <Route path="/voyage-liquidator" element={<VoyageLiquidator_V2 />} />
                  <Route path="/audit-engine" element={<AuditEngine_V2 />} />
                  <Route path="/audit-final" element={<AuditFinal_V2 />} />
                  <Route path="/system-flowchart" element={<SystemFlowchartViewer_V2 />} />
                  <Route path="/system-documentation" element={<SystemDocumentation_V2 />} />
                  <Route path="/company-organigram" element={<CompanyOrganigram_V2 />} />
                  <Route path="/mof-manual" element={<MofManual_V2 />} />
                  <Route path="/sota-petral-one" element={<SotaPetralOne_V2 />} />
              </Route>

              {/* Panel de Gestión de Seguridad y Administración (Sólo ADMIN) */}
              <Route path="/users" element={<ProtectedRoute requireAdmin={true}><UsersPermissions /></ProtectedRoute>} />
              <Route path="/device-vault" element={<ProtectedRoute requireAdmin={true}><DeviceVaultMaster /></ProtectedRoute>} />
              <Route path="/audit-ledger" element={<ProtectedRoute requireAdmin={true}><AuditMaster_V2 /></ProtectedRoute>} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ForecastProvider_V2>
        </AuthProvider>
      </div>
    </Router>
  );
}

export default App_V2;
